import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
} from "@mui/material";
import {
  Edit as EditIcon,
  History as HistoryIcon,
  PersonAdd,
  Search as SearchIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  StickyNote2Outlined as NotesIcon,
  CallMerge as MergeIcon,
  FileUploadOutlined as ImportIcon,
} from "@mui/icons-material";
import { baseAxios } from "../../utils/config";
import { toast } from "react-hot-toast";
import {
  PaginatedTable,
  CreateClientForm,
  EditClientForm,
} from "../../components";
import { useAuth } from "../../contexts/auth";
import { colors } from "..";
import { isClientAdmin } from "./utils";
import HistoryDialog from "./HistoryDialog";
import NoteDialog from "./NoteDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import MergeDialog from "./MergeDialog";
import MergeActionBar from "./MergeActionBar";
import ImportClientsDialog from "./ImportClientsDialog";

const BASE_COLUMNS = [
  { id: "client_first_name", label: "First Name", field: "client_first_name" },
  { id: "client_last_name", label: "Last Name", field: "client_last_name" },
  {
    id: "client_mobile_phone_number",
    label: "Phone number",
    field: "client_mobile_phone_number",
  },
  { id: "client_email", label: "Email", field: "client_email", minWidth: 180 },
  {
    id: "location_name",
    label: "Location",
    field: "location_name",
    minWidth: 180,
  },
  { id: "actions", label: "Actions", sortable: false, minWidth: 200 },
];

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "i");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HighlightPhone({ display, query }) {
  const str = String(display ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const queryDigits = q.replace(/\D/g, "");

  if (!queryDigits) return <HighlightText text={str} query={q} />;

  const digitIndexToCharIndex = [];
  for (let ci = 0; ci < str.length; ci++) {
    if (/\d/.test(str[ci])) digitIndexToCharIndex.push(ci);
  }

  const strDigits = digitIndexToCharIndex.map((ci) => str[ci]).join("");
  const matchStart = strDigits.indexOf(queryDigits);

  if (matchStart === -1) {
    return <HighlightText text={str} query={q} />;
  }

  const matchEnd = matchStart + queryDigits.length - 1;
  const charStart = digitIndexToCharIndex[matchStart];
  const charEnd = digitIndexToCharIndex[matchEnd];

  return [
    str.slice(0, charStart),
    <mark key="m" style={MARK_STYLE}>
      {str.slice(charStart, charEnd + 1)}
    </mark>,
    str.slice(charEnd + 1),
  ];
}

const formatPhoneNumber = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 10) return value ?? "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
};

const normalizeSearchQuery = (value) => {
  const query = String(value ?? "").trim();
  if (!/\d/.test(query)) return query;
  return query.replace(/[\s(),-]/g, "");
};

const COMPACT_BTN_SX = {
  minHeight: 24,
  py: 0.25,
  px: 1,
  textTransform: "none",
  fontSize: "0.75rem",
  "& .MuiButton-startIcon": { mr: 0.5 },
};

export default function ManageClients() {
  const { user } = useAuth();
  const admin = isClientAdmin(user?.userRoles);

  const [clients, setClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("client_last_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(15);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [clientIdToEdit, setClientIdToEdit] = useState(null);

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [clientForHistory, setClientForHistory] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [clientForNote, setClientForNote] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Status filter: "active" | "deleted" | "all"
  const [statusFilter, setStatusFilter] = useState("active");

  // Delete feature
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Merge feature
  const [mergeMode, setMergeMode] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Monotonic id so only the most recently started request can update state.
  // Prevents a slower earlier response (e.g. an unfiltered mount fetch) from
  // overwriting a newer one (e.g. a search) — the cause of the result "flicker".
  const requestIdRef = useRef(0);

  const fetchClients = useCallback(
    (page, rowsPerPage, orderBy = sortBy, order = sortOrder) => {
      const reqId = ++requestIdRef.current;
      setLoading(true);
      const normalizedSearch = normalizeSearchQuery(debouncedSearch);
      const params = {
        page,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };
      if (normalizedSearch) params.search = normalizedSearch;
      if (statusFilter !== "active") params.status = statusFilter;
      setCurrentPage(page);
      setCurrentRowsPerPage(rowsPerPage);
      baseAxios
        .get("/clients/list", { params })
        .then((res) => {
          if (reqId !== requestIdRef.current) return; // stale response, ignore
          setClients(res?.data?.Items ?? []);
          setTotalCount(res?.data?.totalCount ?? 0);
        })
        .catch(() => {
          if (reqId !== requestIdRef.current) return;
          setClients([]);
          setTotalCount(0);
          toast.error("Error loading clients", { duration: 3000 });
        })
        .finally(() => {
          if (reqId !== requestIdRef.current) return;
          setLoading(false);
        });
    },
    [sortBy, sortOrder, debouncedSearch, statusFilter]
  );

  const refetchCurrentPage = useCallback(() => {
    fetchClients(currentPage, currentRowsPerPage);
  }, [fetchClients, currentPage, currentRowsPerPage]);

  const handleSortChange = useCallback(
    (newSortBy, newSortOrder, rowsPerPage) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      setSelectedClients([]);
      const reqId = ++requestIdRef.current;
      setLoading(true);
      const normalizedSearch = normalizeSearchQuery(debouncedSearch);
      const params = {
        page: 0,
        limit: rowsPerPage,
        sortBy: newSortBy,
        sortOrder: newSortOrder,
      };
      if (normalizedSearch) params.search = normalizedSearch;
      if (statusFilter !== "active") params.status = statusFilter;
      baseAxios
        .get("/clients/list", { params })
        .then((res) => {
          if (reqId !== requestIdRef.current) return;
          setClients(res?.data?.Items ?? []);
          setTotalCount(res?.data?.totalCount ?? 0);
        })
        .catch(() => {
          if (reqId !== requestIdRef.current) return;
          setClients([]);
          setTotalCount(0);
          toast.error("Error loading clients", { duration: 3000 });
        })
        .finally(() => {
          if (reqId !== requestIdRef.current) return;
          setLoading(false);
        });
    },
    [debouncedSearch, statusFilter]
  );

  // Initial load + re-fetch when the status filter changes. (A dependency-array
  // effect also runs on mount, so this covers the first load — no separate
  // mount-only effect, which previously caused a duplicate request per mount.)
  useEffect(() => {
    fetchClients(0, 15);
    setMergeMode(false);
    setSelectedClients([]);
  }, [statusFilter]);

  // Exit merge mode when search changes
  useEffect(() => {
    setMergeMode(false);
    setSelectedClients([]);
  }, [debouncedSearch]);

  // ---- Existing handlers ----

  const openCreateClient = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const closeCreateClient = useCallback(() => {
    setCreateDialogOpen(false);
  }, []);

  const onClientCreated = useCallback(() => {
    closeCreateClient();
    fetchClients(0, currentRowsPerPage);
  }, [closeCreateClient, fetchClients, currentRowsPerPage]);

  const handleEditClick = useCallback((row) => {
    setClientIdToEdit(row.client_id);
    setEditDialogOpen(true);
  }, []);

  const closeEditClient = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditDialogExited = useCallback(() => {
    setClientIdToEdit(null);
  }, []);

  const onClientUpdated = useCallback(() => {
    closeEditClient();
    refetchCurrentPage();
  }, [closeEditClient, refetchCurrentPage]);

  const handleHistoryClick = (row) => {
    setClientForHistory(row);
    setHistoryDialogOpen(true);
  };

  const handleNotesClick = (row) => {
    setClientForNote(row);
    setNoteDialogOpen(true);
  };

  // ---- Delete handlers ----

  const handleDeleteClick = (row) => {
    setClientToDelete(row);
    setDeleteDialogOpen(true);
  };

  // ---- Restore handler ----

  const handleRestore = useCallback(
    async (client) => {
      try {
        await baseAxios.post(`/clients/${client.client_id}/restore`);
        toast.success(
          `${client.client_first_name} ${client.client_last_name} restored`,
          { duration: 3000 }
        );
        refetchCurrentPage();
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to restore client",
          { duration: 3000 }
        );
      }
    },
    [refetchCurrentPage]
  );

  const handleDeleteConfirm = useCallback(
    async (client) => {
      setDeleteLoading(true);
      try {
        const res = await baseAxios.delete(`/clients/${client.client_id}`);
        setDeleteDialogOpen(false);
        setClientToDelete(null);

        const name =
          res?.data?.client_name ||
          `${client.client_first_name} ${client.client_last_name}`;

        // Re-fetch current page
        refetchCurrentPage();

        // Toast with undo
        toast.custom(
          (t) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: "#333",
                color: "#fff",
                px: 2,
                py: 1.5,
                borderRadius: 2,
                boxShadow: 3,
                minWidth: 300,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {name} deleted
              </Typography>
              <Button
                size="small"
                variant="text"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  textTransform: "none",
                }}
                onClick={() => {
                  toast.dismiss(t.id);
                  handleRestore(client);
                }}
              >
                UNDO
              </Button>
            </Box>
          ),
          { duration: 8000 }
        );
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to delete client",
          { duration: 3000 }
        );
      } finally {
        setDeleteLoading(false);
      }
    },
    [refetchCurrentPage, handleRestore]
  );

  // ---- Merge handlers ----

  const handleToggleSelect = useCallback((row) => {
    setSelectedClients((prev) => {
      const exists = prev.find((c) => c.client_id === row.client_id);
      if (exists) return prev.filter((c) => c.client_id !== row.client_id);
      if (prev.length >= 2) return prev;
      return [...prev, row];
    });
  }, []);

  const handleMergeConfirm = useCallback(
    async (primaryClientId) => {
      setMergeLoading(true);
      const secondaryClient = selectedClients.find(
        (c) => c.client_id !== primaryClientId
      );
      try {
        const res = await baseAxios.post("/clients/merge", {
          primary_client_id: primaryClientId,
          secondary_client_id: secondaryClient.client_id,
        });
        setMergeDialogOpen(false);
        setMergeMode(false);
        setSelectedClients([]);

        const transferred = res?.data?.transferred;
        const parts = [];
        if (transferred?.schedule_entries)
          parts.push(`${transferred.schedule_entries} appointment(s)`);
        if (transferred?.service_receipts)
          parts.push(`${transferred.service_receipts} receipt(s)`);
        if (transferred?.transactions)
          parts.push(`${transferred.transactions} transaction(s)`);

        const summary = parts.length
          ? `Clients merged. Transferred: ${parts.join(", ")}`
          : "Clients merged successfully";

        toast.success(summary, { duration: 6000 });
        refetchCurrentPage();
      } catch (err) {
        toast.error(
          err?.response?.data?.message || "Failed to merge clients",
          { duration: 4000 }
        );
      } finally {
        setMergeLoading(false);
      }
    },
    [selectedClients, refetchCurrentPage]
  );

  // ---- Build columns ----

  const columns = useMemo(() => {
    const sq = debouncedSearch;
    const showingDeleted = statusFilter === "deleted";
    const showingAll = statusFilter === "all";
    const cols = [];

    // Checkbox column for merge (admin only, not in deleted view)
    if (admin && mergeMode) {
      cols.push({
        id: "select",
        label: "",
        sortable: false,
        width: 48,
        minWidth: 48,
        render: (_, row) => {
          const isSelected = selectedClients.some(
            (c) => c.client_id === row.client_id
          );
          const isDisabled = !isSelected && selectedClients.length >= 2;
          return (
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={isDisabled}
              onChange={() => handleToggleSelect(row)}
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0 }}
            />
          );
        },
      });
    }

    // Map base columns with modifications
    cols.push(
      ...BASE_COLUMNS.map((col) => {
        if (col.id === "actions") {
          return {
            ...col,
            render: (_, row) => {
              const isDeleted = row.is_deleted === 1 || row.is_deleted === true;
              return (
                <Box
                  component="span"
                  sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
                >
                  {/* Edit — hidden for deleted clients */}
                  {!isDeleted && !showingDeleted && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleEditClick(row)}
                      sx={{
                        ...COMPACT_BTN_SX,
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.text.primary
                            : colors.black,
                        "& .MuiButton-startIcon": {
                          mr: 0.5,
                          color: "primary.main",
                        },
                      }}
                    >
                      Edit
                    </Button>
                  )}

                  {/* History — always shown */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleHistoryClick(row)}
                    sx={{
                      ...COMPACT_BTN_SX,
                      color: "text.secondary",
                      borderColor: "divider",
                      "&:hover": {
                        borderColor: "text.secondary",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    History
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<NotesIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleNotesClick(row)}
                    sx={{
                      ...COMPACT_BTN_SX,
                      color: "text.secondary",
                      borderColor: "divider",
                      "&:hover": {
                        borderColor: "text.secondary",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    Notes
                  </Button>

                  {/* Delete — admin, active or "all" view non-deleted rows */}
                  {admin && !isDeleted && !showingDeleted && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(row)}
                      title="Delete client"
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}

                  {/* Restore — admin, deleted rows */}
                  {admin && (showingDeleted || (showingAll && isDeleted)) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<RestoreIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleRestore(row)}
                      sx={COMPACT_BTN_SX}
                    >
                      Restore
                    </Button>
                  )}
                </Box>
              );
            },
          };
        }
        if (col.id === "client_mobile_phone_number") {
          return {
            ...col,
            render: (value) => {
              const display = formatPhoneNumber(value);
              return display ? (
                <HighlightPhone display={display} query={sq} />
              ) : (
                "-"
              );
            },
          };
        }
        return {
          ...col,
          render: (value) =>
            value ? <HighlightText text={String(value)} query={sq} /> : "-",
        };
      })
    );

    return cols;
  }, [
    debouncedSearch,
    admin,
    statusFilter,
    mergeMode,
    selectedClients,
    handleToggleSelect,
    handleRestore,
    handleEditClick,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Clients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit client details and view appointment history
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {admin && (
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, val) => {
                if (val !== null) setStatusFilter(val);
              }}
              size="small"
              sx={{
                height: 40,
                bgcolor: "background.paper",
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 1.5,
                },
              }}
            >
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="deleted">Deleted</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          )}
          <TextField
            size="small"
            placeholder="Search by name, email, or phone"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 300,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {admin && statusFilter !== "deleted" && (
            <Button
              variant={mergeMode ? "contained" : "outlined"}
              size="small"
              color={mergeMode ? "primary" : "secondary"}
              startIcon={<MergeIcon />}
              onClick={() => {
                setMergeMode((prev) => !prev);
                setSelectedClients([]);
              }}
              sx={{
                height: 40,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Merge
            </Button>
          )}
          {admin && (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<ImportIcon />}
              onClick={() => setImportDialogOpen(true)}
              sx={{
                height: 40,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Import
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={openCreateClient}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <PersonAdd sx={{ mr: 0.75 }} /> Add Client
          </Button>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={clients}
        totalRowCount={totalCount}
        loading={loading}
        onPaginationChange={fetchClients}
        onSortChange={handleSortChange}
        initialOrderBy="client_last_name"
        initialOrder="asc"
        searchQuery={debouncedSearch}
      />

      {/* Merge action bar */}
      {admin && mergeMode && selectedClients.length > 0 && (
        <MergeActionBar
          selectedCount={selectedClients.length}
          onCancel={() => { setMergeMode(false); setSelectedClients([]); }}
          onMerge={() => setMergeDialogOpen(true)}
        />
      )}

      {/* Dialogs */}
      <Dialog
        open={createDialogOpen}
        onClose={closeCreateClient}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <CreateClientForm
          key={createDialogOpen ? "create-client-open" : "create-client-closed"}
          onSaved={onClientCreated}
          onRequestClose={closeCreateClient}
        />
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={closeEditClient}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        TransitionProps={{ onExited: handleEditDialogExited }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <EditClientForm
          key={
            clientIdToEdit == null
              ? "edit-client"
              : `edit-client-${clientIdToEdit}`
          }
          clientId={clientIdToEdit}
          onSaved={onClientUpdated}
          onRequestClose={closeEditClient}
        />
      </Dialog>

      <HistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setClientForHistory(null);
        }}
        client={clientForHistory}
      />

      <NoteDialog
        open={noteDialogOpen}
        onClose={() => {
          setNoteDialogOpen(false);
          setClientForNote(null);
        }}
        client={clientForNote}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setClientToDelete(null);
        }}
        client={clientToDelete}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />

      <MergeDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        clients={selectedClients}
        onConfirm={handleMergeConfirm}
        loading={mergeLoading}
      />

      <ImportClientsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={() =>
          fetchClients(currentPage, currentRowsPerPage, sortBy, sortOrder)
        }
      />
    </Box>
  );
}
